(function () {
  'use strict';

  const controllerKey = '__slikhaarMaxOne';

  if (window[controllerKey]) {
    window[controllerKey].refresh(document);
    return;
  }

  const taggedContainerSelector = '[data-product-tags], [data-line-tags]';
  const quantityInputSelector = [
    'input[name="quantity"]',
    'input.quantity-input[is="quantity-input"]',
    'input[name="updates[]"]',
    'input.qty',
    'input.quantity__input'
  ].join(', ');
  const increaseButtonSelector = [
    'button[name="plus"]',
    'button.quantity__button--plus',
    '[data-action="increase-quantity"]'
  ].join(', ');

  const pendingCartUpdates = new Map();
  let cartUpdateScheduled = false;

  const log = (...args) => {
    if (window.MAX_ONE_DEBUG) {
      console.log('[max-one]', ...args);
    }
  };

  function normalizeTags(value) {
    return String(value || '')
      .toLowerCase()
      .split(',')
      .map((tag) => tag.trim().replace(/[^a-z0-9]+/g, ''))
      .filter(Boolean);
  }

  function hasMaxOneTag(container) {
    if (!(container instanceof Element)) {
      return false;
    }

    const tags = normalizeTags(container.getAttribute('data-product-tags') || container.getAttribute('data-line-tags'));
    return tags.includes('max1') || tags.includes('maxone');
  }

  function findTaggedContainer(element) {
    return element instanceof Element ? element.closest(taggedContainerSelector) : null;
  }

  function isCartQuantityInput(input) {
    return input.hasAttribute('data-line-key') || Boolean(input.closest('[data-line-tags]'));
  }

  function constrainInput(input) {
    if (!(input instanceof HTMLInputElement)) {
      return false;
    }

    const parsedValue = Number.parseInt(input.value, 10);
    const changed = parsedValue !== 1;

    input.min = '1';
    input.max = '1';
    input.step = '1';

    if (changed) {
      input.value = '1';
      input.defaultValue = '1';
    }

    return changed;
  }

  function scheduleCartUpdate(input) {
    const updateKey = input.getAttribute('data-line-key') || input;

    if (!pendingCartUpdates.has(updateKey)) {
      pendingCartUpdates.set(updateKey, input);
    }

    if (cartUpdateScheduled) {
      return;
    }

    cartUpdateScheduled = true;

    queueMicrotask(() => {
      cartUpdateScheduled = false;

      for (const cartInput of pendingCartUpdates.values()) {
        cartInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      pendingCartUpdates.clear();
    });
  }

  function isIncreaseButton(button) {
    if (button.matches(increaseButtonSelector)) {
      return true;
    }

    const quantitySelector = button.closest('quantity-selector');
    return Boolean(quantitySelector && button === quantitySelector.querySelector('.quantity-selector__button:last-of-type'));
  }

  function disableIncreaseButtons(container) {
    const buttons = new Set(container.querySelectorAll(increaseButtonSelector));

    container.querySelectorAll('quantity-selector').forEach((quantitySelector) => {
      const increaseButton = quantitySelector.querySelector('.quantity-selector__button:last-of-type');

      if (increaseButton) {
        buttons.add(increaseButton);
      }
    });

    buttons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });
  }

  function enforceContainer(container) {
    if (!hasMaxOneTag(container)) {
      return;
    }

    container.querySelectorAll(quantityInputSelector).forEach((input) => {
      const changed = constrainInput(input);

      if (changed && isCartQuantityInput(input)) {
        scheduleCartUpdate(input);
      }
    });

    disableIncreaseButtons(container);
    log('Enforced quantity limit on', container);
  }

  function refresh(root) {
    const containers = new Set();

    if (root instanceof Element) {
      if (root.matches(taggedContainerSelector)) {
        containers.add(root);
      }

      const ancestor = findTaggedContainer(root);

      if (ancestor) {
        containers.add(ancestor);
      }
    }

    if (root instanceof Document || root instanceof DocumentFragment || root instanceof Element) {
      root.querySelectorAll(taggedContainerSelector).forEach((container) => containers.add(container));
    }

    containers.forEach(enforceContainer);
  }

  document.addEventListener(
    'input',
    (event) => {
      const input = event.target;

      if (!(input instanceof HTMLInputElement) || !input.matches(quantityInputSelector)) {
        return;
      }

      const container = findTaggedContainer(input);

      if (hasMaxOneTag(container)) {
        constrainInput(input);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;

      if (!(input instanceof HTMLInputElement) || !input.matches(quantityInputSelector)) {
        return;
      }

      const container = findTaggedContainer(input);

      if (hasMaxOneTag(container)) {
        constrainInput(input);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;

      if (!button || !isIncreaseButton(button) || !hasMaxOneTag(findTaggedContainer(button))) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement) || !hasMaxOneTag(findTaggedContainer(form))) {
        return;
      }

      Array.from(form.elements).forEach((field) => {
        if (field instanceof HTMLInputElement && field.matches(quantityInputSelector)) {
          constrainInput(field);
        }
      });
    },
    true
  );

  document.addEventListener('formdata', (event) => {
    if (event.target instanceof HTMLFormElement && hasMaxOneTag(findTaggedContainer(event.target))) {
      event.formData.set('quantity', '1');
    }
  });

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:reorder', (event) => refresh(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element || node instanceof DocumentFragment) {
          refresh(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  window[controllerKey] = { refresh };
  refresh(document);
})();
